import type { Obj, Page, Deck } from "../../shared/types";
import { img, cardFace, cardBack, MARKERS, COLOR_MARKERS, roundRect } from "./images";
import { visibility, wallSegs, pointInPoly, objBounds, unitsToPx, measure, type Seg } from "./geometry";

export interface Cam { x: number; y: number; z: number }

export interface Ruler { pts: number[]; shape: "line" | "circle" | "cone" | "square"; color: string; name?: string }
export interface Ping { x: number; y: number; color: string; t0: number }

export interface Preview {
  kind: "path" | "rect" | "ellipse" | "poly" | "marquee" | "fog" | "wall" | "line";
  pts: number[];
  closed?: boolean;
  mode?: "reveal" | "hide";
  door?: boolean;
}

export interface View {
  page: Page;
  objs: Obj[];
  decks: Record<string, Deck & { pile: any }>;
  cam: Cam;
  w: number;
  h: number;
  dpr: number;
  gm: boolean;
  layer: string;
  tool: string;
  selected: Set<string>;
  controls: (o: Obj) => boolean;
  preview: Preview | null;
  rulers: Ruler[];
  pings: Ping[];
  gmDark: number;
  explored: HTMLCanvasElement | null;
  style: { stroke: string; fill: string; strokeWidth: number };
  hoverDoor: string | null;
}

const BAR_COLORS = ["#2eb82e", "#2f6fdf", "#d93030"];
let darkCanvas: HTMLCanvasElement | null = null;
let fogCanvas: HTMLCanvasElement | null = null;

function offscreen(c: HTMLCanvasElement | null, w: number, h: number) {
  if (!c) c = document.createElement("canvas");
  if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
  return c;
}

export const EXPLORE_SCALE = 0.25;

/** Returns a predicate telling whether a world point is visible to the viewer (players under lighting). */
export function draw(ctx: CanvasRenderingContext2D, v: View): (x: number, y: number) => boolean {
  const { page, cam, dpr, gm } = v;
  const pw = page.width * page.grid, ph = page.height * page.grid;
  const W = Math.round(v.w * dpr), H = Math.round(v.h * dpr);
  const world = (g: CanvasRenderingContext2D) => g.setTransform(dpr * cam.z, 0, 0, dpr * cam.z, -cam.x * cam.z * dpr, -cam.y * cam.z * dpr);

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = "#1c1b20";
  ctx.fillRect(0, 0, W, H);
  world(ctx);
  ctx.fillStyle = page.bg || "#fff";
  ctx.fillRect(0, 0, pw, ph);

  const byZ = (a: Obj, b: Obj) => a.z - b.z;
  const layerObjs = (l: string) => v.objs.filter(o => o.layer === l).sort(byZ);
  const mapL = layerObjs("map"), objL = layerObjs("objects"), gmL = gm ? layerObjs("gm") : [], walls = v.objs.filter(o => o.layer === "walls");

  // ---- lighting prep ----
  let visible: (x: number, y: number) => boolean = () => true;
  let lightPolys: { x: number; y: number; bright: number; dim: number; poly: number[] }[] = [];
  let viewers: { x: number; y: number; range: number; dark: number; poly: number[] }[] = [];
  let segs: Seg[] = [];
  if (page.lighting) {
    segs = wallSegs(walls);
    let hsum = 0;
    for (const s of segs) hsum = (hsum * 31 + s[0] * 7 + s[1] * 13 + s[2] * 17 + s[3] * 19) % 1e9;
    const wkey = `${page.id}:${segs.length}:${hsum}`;
    const maxR = Math.hypot(pw, ph);
    const sources = [...objL, ...(gm ? gmL : [])];
    for (const o of v.objs) {
      if (o.layer === "walls" || !((o.lightBright || 0) + (o.lightDim || 0) > 0)) continue;
      const b = unitsToPx(page, o.lightBright || 0), d = unitsToPx(page, o.lightDim || 0);
      const c = { x: o.x + o.w / 2, y: o.y + o.h / 2 };
      lightPolys.push({ ...c, bright: b, dim: d, poly: visibility(c.x, c.y, b + d, segs, wkey) });
    }
    for (const o of sources) {
      if (!o.vision || o.layer !== "objects") continue;
      if (!gm && !v.controls(o)) continue;
      if (gm && !(o.controlledBy?.length || o.represents)) continue;
      const c = { x: o.x + o.w / 2, y: o.y + o.h / 2 };
      const range = o.visionRange ? unitsToPx(page, o.visionRange) : maxR;
      viewers.push({ ...c, range, dark: unitsToPx(page, o.darkvision || 0), poly: visibility(c.x, c.y, range, segs, wkey) });
    }
    if (!gm) {
      visible = (x, y) =>
        viewers.some(vw => {
          if (Math.hypot(x - vw.x, y - vw.y) > vw.range || !pointInPoly(x, y, vw.poly)) return false;
          if (page.globalIllum) return true;
          if (Math.hypot(x - vw.x, y - vw.y) <= vw.dark) return true;
          return lightPolys.some(l => Math.hypot(x - l.x, y - l.y) <= l.bright + l.dim && pointInPoly(x, y, l.poly));
        });
    }
  }

  // ---- map layer ----
  for (const o of mapL) drawObj(ctx, o, v);

  // ---- grid ----
  if (page.showGrid && page.grid * cam.z > 6) {
    ctx.save();
    ctx.globalAlpha = page.gridOpacity ?? 0.25;
    ctx.strokeStyle = page.gridColor || "#000";
    ctx.lineWidth = 1 / cam.z;
    ctx.beginPath();
    const x0 = Math.max(0, Math.floor(cam.x / page.grid)), x1 = Math.min(page.width, Math.ceil((cam.x + v.w / cam.z) / page.grid));
    const y0 = Math.max(0, Math.floor(cam.y / page.grid)), y1 = Math.min(page.height, Math.ceil((cam.y + v.h / cam.z) / page.grid));
    for (let i = x0; i <= x1; i++) { ctx.moveTo(i * page.grid, 0); ctx.lineTo(i * page.grid, ph); }
    for (let j = y0; j <= y1; j++) { ctx.moveTo(0, j * page.grid); ctx.lineTo(pw, j * page.grid); }
    ctx.stroke();
    ctx.restore();
  }

  // ---- auras ----
  const tokenVisible = (o: Obj) => gm || !page.lighting || v.controls(o) || o.type !== "image" || visible(o.x + o.w / 2, o.y + o.h / 2);
  for (const o of [...objL, ...gmL]) {
    if (!o.auras || !tokenVisible(o)) continue;
    for (const a of o.auras) {
      if (!a || !(a.r > 0)) continue;
      if (!gm && a.show === false && !v.controls(o)) continue;
      const r = unitsToPx(page, a.r) + Math.max(o.w, o.h) / 2;
      ctx.save();
      ctx.globalAlpha = o.layer === "gm" ? 0.15 : 0.25;
      ctx.fillStyle = a.color || "#ffcc00";
      ctx.beginPath();
      if (a.square) ctx.rect(o.x + o.w / 2 - r, o.y + o.h / 2 - r, r * 2, r * 2);
      else ctx.arc(o.x + o.w / 2, o.y + o.h / 2, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  // ---- tokens / objects ----
  const drawnTokens: Obj[] = [];
  for (const o of objL) {
    if (!tokenVisible(o)) continue;
    drawObj(ctx, o, v);
    drawnTokens.push(o);
  }
  if (gm) {
    ctx.save();
    ctx.globalAlpha = v.layer === "gm" ? 0.85 : 0.55;
    for (const o of gmL) drawObj(ctx, o, v);
    ctx.restore();
    drawnTokens.push(...gmL);
  }

  // door handles visible to players (under the darkness so unseen doors stay hidden)
  if (!gm && page.lighting) for (const w of walls) if (w.door) drawDoorHandle(ctx, w, cam.z, v.hoverDoor === w.id);


  // ---- darkness / lighting ----
  if (page.lighting && (!gm || v.gmDark > 0)) {
    darkCanvas = offscreen(darkCanvas, W, H);
    const g = darkCanvas.getContext("2d")!;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.globalCompositeOperation = "source-over";
    g.clearRect(0, 0, W, H);
    g.fillStyle = "#000";
    g.fillRect(0, 0, W, H);
    if (page.explorer && v.explored && !gm) {
      g.globalCompositeOperation = "destination-out";
      g.globalAlpha = 0.5;
      world(g);
      g.imageSmoothingEnabled = true;
      g.drawImage(v.explored, 0, 0, v.explored.width / EXPLORE_SCALE, v.explored.height / EXPLORE_SCALE);
      g.globalAlpha = 1;
    }
    world(g);
    g.globalCompositeOperation = "destination-out";
    paintVisible(g, page, viewers, lightPolys, pw, ph);
    if (page.explorer && v.explored && !gm) {
      const e = v.explored.getContext("2d")!;
      e.setTransform(EXPLORE_SCALE, 0, 0, EXPLORE_SCALE, 0, 0);
      e.globalCompositeOperation = "source-over";
      paintVisible(e, page, viewers, lightPolys, pw, ph, true);
    }
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = gm ? v.gmDark : 1;
    ctx.drawImage(darkCanvas, 0, 0);
    ctx.restore();
  }

  // ---- manual fog of war ----
  if (page.fogEnabled) {
    fogCanvas = offscreen(fogCanvas, W, H);
    const g = fogCanvas.getContext("2d")!;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.globalCompositeOperation = "source-over";
    g.clearRect(0, 0, W, H);
    world(g);
    g.fillStyle = "#000";
    g.fillRect(0, 0, pw, ph);
    for (const op of page.fog || []) {
      g.globalCompositeOperation = op.mode === "reveal" ? "destination-out" : "source-over";
      polyPath(g, op.pts);
      g.fill();
    }
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = gm ? 0.4 : 1;
    ctx.drawImage(fogCanvas, 0, 0);
    ctx.restore();
  }

  world(ctx);
  // manual fog hides points under "hide" areas (last matching op wins)
  const fogged = (x: number, y: number) => {
    if (!page.fogEnabled) return false;
    let hidden = true;
    for (const op of page.fog || []) if (pointInPoly(x, y, op.pts)) hidden = op.mode === "hide";
    return hidden;
  };
  const seen = (x: number, y: number) => gm || (visible(x, y) && !fogged(x, y));

  // ---- token decorations (names, bars, markers) — above the darkness so they stay readable ----
  for (const o of drawnTokens) {
    if (o.type !== "image" && o.type !== "card") continue;
    if (!gm && !v.controls(o) && !seen(o.x + o.w / 2, o.y + o.h / 2)) continue;
    drawDecor(ctx, o, v);
  }

  // ---- walls (GM) ----
  if (gm && (v.layer === "walls" || v.tool === "wall" || v.tool === "door" || page.lighting)) {
    const strong = v.layer === "walls" || v.tool === "wall" || v.tool === "door";
    for (const w of walls) {
      const p = w.pts || [];
      ctx.save();
      ctx.globalAlpha = strong ? 1 : 0.35;
      ctx.strokeStyle = w.door ? (w.open ? "#3c3" : "#f90") : "#3af";
      ctx.lineWidth = (strong ? 4 : 2) / cam.z;
      if (w.door && w.open) ctx.setLineDash([8 / cam.z, 6 / cam.z]);
      ctx.beginPath();
      for (let i = 0; i < p.length; i += 2) i ? ctx.lineTo(p[i], p[i + 1]) : ctx.moveTo(p[i], p[i + 1]);
      ctx.stroke();
      ctx.restore();
      if (w.door) drawDoorHandle(ctx, w, cam.z, v.hoverDoor === w.id);
    }
  }

  // ---- selection ----
  for (const o of v.objs) if (v.selected.has(o.id)) drawSelection(ctx, o, cam.z, v.selected.size === 1);

  // ---- previews ----
  if (v.preview) drawPreview(ctx, v.preview, v);

  // ---- rulers ----
  for (const r of v.rulers) drawRuler(ctx, r, page, cam.z);

  // ---- pings ----
  const t = performance.now();
  for (const p of v.pings) {
    const age = (t - p.t0) / 1000;
    for (let k = 0; k < 3; k++) {
      const a = age - k * 0.25;
      if (a < 0 || a > 1.2) continue;
      ctx.save();
      ctx.globalAlpha = 1 - a / 1.2;
      ctx.strokeStyle = p.color;
      ctx.lineWidth = 4 / cam.z;
      ctx.beginPath();
      ctx.arc(p.x, p.y, (10 + a * 80) / cam.z, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }
  return seen;
}

function paintVisible(g: CanvasRenderingContext2D, page: Page, viewers: any[], lights: any[], pw: number, ph: number, solid = false) {
  for (const vw of viewers) {
    g.save();
    polyPath(g, vw.poly);
    g.clip();
    if (vw.range < 1e6) { g.beginPath(); g.arc(vw.x, vw.y, vw.range, 0, Math.PI * 2); g.clip(); }
    if (page.globalIllum) {
      g.fillStyle = "#000";
      g.fillRect(0, 0, pw, ph);
    } else {
      for (const l of lights) {
        const R = l.bright + l.dim;
        if (R <= 0) continue;
        g.save();
        polyPath(g, l.poly);
        g.clip();
        const grad = g.createRadialGradient(l.x, l.y, 0, l.x, l.y, R);
        const b = l.bright / R;
        grad.addColorStop(0, "rgba(0,0,0,1)");
        if (b > 0 && b < 1) {
          grad.addColorStop(Math.max(0, b - 0.02), "rgba(0,0,0,1)");
          grad.addColorStop(Math.min(1, b + 0.02), solid ? "rgba(0,0,0,1)" : "rgba(0,0,0,0.55)");
        } else if (b === 0) grad.addColorStop(0.01, solid ? "rgba(0,0,0,1)" : "rgba(0,0,0,0.55)");
        grad.addColorStop(0.95, solid ? "rgba(0,0,0,1)" : b >= 1 ? "rgba(0,0,0,1)" : "rgba(0,0,0,0.45)");
        grad.addColorStop(1, "rgba(0,0,0,0)");
        g.fillStyle = grad;
        g.beginPath();
        g.arc(l.x, l.y, R, 0, Math.PI * 2);
        g.fill();
        g.restore();
      }
      if (vw.dark > 0) {
        const grad = g.createRadialGradient(vw.x, vw.y, 0, vw.x, vw.y, vw.dark);
        grad.addColorStop(0, solid ? "rgba(0,0,0,1)" : "rgba(0,0,0,0.6)");
        grad.addColorStop(0.9, solid ? "rgba(0,0,0,1)" : "rgba(0,0,0,0.6)");
        grad.addColorStop(1, "rgba(0,0,0,0)");
        g.fillStyle = grad;
        g.beginPath();
        g.arc(vw.x, vw.y, vw.dark, 0, Math.PI * 2);
        g.fill();
      }
    }
    g.restore();
  }
}

export function polyPath(g: CanvasRenderingContext2D, pts: number[]) {
  g.beginPath();
  for (let i = 0; i < pts.length; i += 2) i ? g.lineTo(pts[i], pts[i + 1]) : g.moveTo(pts[i], pts[i + 1]);
  g.closePath();
}

function drawDoorHandle(ctx: CanvasRenderingContext2D, w: Obj, z: number, hover: boolean) {
  const p = w.pts || [];
  if (p.length < 4) return;
  const mx = (p[0] + p[p.length - 2]) / 2, my = (p[1] + p[p.length - 1]) / 2;
  const r = 9 / z;
  ctx.save();
  ctx.fillStyle = w.open ? "#2a2" : "#c70";
  ctx.strokeStyle = hover ? "#fff" : "#222";
  ctx.lineWidth = 2 / z;
  ctx.beginPath();
  ctx.arc(mx, my, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = "#fff";
  ctx.font = `${12 / z}px sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(w.open ? "⇆" : "▮", mx, my + 0.5 / z);
  ctx.restore();
}

export function doorHandleAt(walls: Obj[], x: number, y: number, z: number): Obj | null {
  for (const w of walls) {
    if (!w.door) continue;
    const p = w.pts || [];
    if (p.length < 4) continue;
    const mx = (p[0] + p[p.length - 2]) / 2, my = (p[1] + p[p.length - 1]) / 2;
    if (Math.hypot(x - mx, y - my) <= 11 / z) return w;
  }
  return null;
}

export function drawObj(ctx: CanvasRenderingContext2D, o: Obj, v: { decks: Record<string, any>; cam: Cam }) {
  if (o.type === "wall") return;
  ctx.save();
  ctx.translate(o.x + o.w / 2, o.y + o.h / 2);
  if (o.rot) ctx.rotate((o.rot * Math.PI) / 180);
  const hw = o.w / 2, hh = o.h / 2;
  switch (o.type) {
    case "image": {
      const im = img(o.src);
      if (o.flipH) ctx.scale(-1, 1);
      if (im) ctx.drawImage(im, -hw, -hh, o.w, o.h);
      else {
        ctx.fillStyle = "rgba(120,120,140,0.5)";
        ctx.fillRect(-hw, -hh, o.w, o.h);
        if (!o.src) {
          ctx.fillStyle = "#fff";
          ctx.font = `${Math.min(o.w, o.h) * 0.4}px sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText((o.name || "?").slice(0, 1).toUpperCase(), 0, 0);
        }
      }
      if (o.tint) {
        ctx.strokeStyle = o.tint;
        ctx.lineWidth = Math.max(3, o.w * 0.05);
        ctx.strokeRect(-hw, -hh, o.w, o.h);
      }
      break;
    }
    case "card": {
      const deck = v.decks[o.deckId || ""];
      let face: CanvasImageSource | null = null;
      if (o.faceUp && o.cardIdx !== undefined && o.cardIdx >= 0 && deck) {
        const c = deck.cards[o.cardIdx];
        face = c?.src ? img(c.src) : c ? cardFace(c.name) : null;
      } else face = deck?.back ? img(deck.back) : cardBack();
      if (face) ctx.drawImage(face, -hw, -hh, o.w, o.h);
      else { ctx.fillStyle = "#eee"; ctx.fillRect(-hw, -hh, o.w, o.h); }
      break;
    }
    case "rect":
    case "ellipse": {
      ctx.beginPath();
      if (o.type === "rect") ctx.rect(-hw, -hh, o.w, o.h);
      else ctx.ellipse(0, 0, hw, hh, 0, 0, Math.PI * 2);
      if (o.fill && o.fill !== "transparent") { ctx.fillStyle = o.fill; ctx.fill(); }
      if (o.stroke && o.stroke !== "transparent" && (o.strokeWidth ?? 0) > 0) { ctx.strokeStyle = o.stroke; ctx.lineWidth = o.strokeWidth!; ctx.stroke(); }
      break;
    }
    case "path": {
      const p = o.pts || [];
      ctx.beginPath();
      for (let i = 0; i < p.length; i += 2) {
        const x = p[i] * o.w - hw, y = p[i + 1] * o.h - hh;
        i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
      }
      if (o.fill && o.fill !== "transparent") { ctx.closePath(); ctx.fillStyle = o.fill; ctx.fill(); }
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      if (o.stroke && o.stroke !== "transparent") { ctx.strokeStyle = o.stroke; ctx.lineWidth = o.strokeWidth || 3; ctx.stroke(); }
      break;
    }
    case "text": {
      const fs = o.fontSize || 28;
      ctx.font = `${fs}px ${o.font || "Georgia, serif"}`;
      ctx.fillStyle = o.stroke || "#000";
      ctx.textBaseline = "top";
      const lines = (o.text || "").split("\n");
      lines.forEach((ln, i) => ctx.fillText(ln, -hw, -hh + i * fs * 1.2));
      break;
    }
  }
  ctx.restore();
}

export function measureText(text: string, fontSize: number, font = "Georgia, serif") {
  const c = (measureText as any).c || ((measureText as any).c = document.createElement("canvas").getContext("2d"));
  c.font = `${fontSize}px ${font}`;
  const lines = (text || " ").split("\n");
  return { w: Math.max(10, ...lines.map((l: string) => c.measureText(l).width)), h: lines.length * fontSize * 1.2 };
}

function drawDecor(ctx: CanvasRenderingContext2D, o: Obj, v: View) {
  const z = v.cam.z;
  const b = objBounds(o);
  const own = v.controls(o);
  // bars
  const bars = (o.bars || []).map((bar, i) => ({ bar, i })).filter(({ bar }) => bar && bar.max !== "" && bar.max != null && (v.gm || own || bar.show) && parseFloat(bar.max) > 0);
  const bh = Math.max(5, 7 / z);
  bars.forEach(({ bar, i }, k) => {
    const y = b.y1 - (bars.length - k) * (bh + 1.5 / z) - 2 / z;
    const frac = Math.max(0, Math.min(1, parseFloat(bar.v) / parseFloat(bar.max)));
    ctx.fillStyle = "rgba(0,0,0,0.6)";
    ctx.fillRect(b.x1, y, b.x2 - b.x1, bh);
    ctx.fillStyle = BAR_COLORS[i];
    ctx.fillRect(b.x1, y, (b.x2 - b.x1) * (Number.isFinite(frac) ? frac : 0), bh);
    if (z > 0.6 && (v.gm || own || bar.show)) {
      ctx.fillStyle = "#fff";
      ctx.font = `bold ${Math.max(8, bh * 0.95)}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(`${bar.v}/${bar.max}`, (b.x1 + b.x2) / 2, y + bh / 2 + 0.5 / z);
    }
  });
  // status markers
  const st = o.statuses || [];
  if (st.length) {
    const s = Math.max(12, Math.min(22, (b.x2 - b.x1) / 3.5));
    st.forEach((raw, i) => {
      const [name, num] = raw.split("@");
      const x = b.x2 - s * (1 + (i % 4)) + s / 2;
      const y = b.y1 + s / 2 + Math.floor(i / 4) * s;
      if (name === "dead") {
        ctx.save();
        ctx.strokeStyle = "rgba(200,0,0,0.85)";
        ctx.lineWidth = Math.max(4, (b.x2 - b.x1) / 12);
        ctx.beginPath();
        ctx.moveTo(b.x1, b.y1); ctx.lineTo(b.x2, b.y2); ctx.moveTo(b.x2, b.y1); ctx.lineTo(b.x1, b.y2);
        ctx.stroke();
        ctx.restore();
        return;
      }
      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, s / 2, 0, Math.PI * 2);
      ctx.fillStyle = COLOR_MARKERS[name] || "rgba(255,255,255,0.9)";
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = "#333";
      ctx.stroke();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      if (MARKERS[name]) {
        ctx.font = `${s * 0.7}px sans-serif`;
        ctx.fillText(MARKERS[name], x, y + 1);
      }
      if (num) {
        ctx.font = `bold ${s * 0.6}px sans-serif`;
        ctx.fillStyle = "#000";
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 2;
        ctx.strokeText(num, x + s * 0.3, y + s * 0.3);
        ctx.fillText(num, x + s * 0.3, y + s * 0.3);
      }
      ctx.restore();
    });
  }
  // name plate
  if (o.showName && o.name) {
    const fs = Math.max(11, 13 / z);
    ctx.font = `600 ${fs}px sans-serif`;
    const tw = ctx.measureText(o.name).width + fs * 0.8;
    const cx = (b.x1 + b.x2) / 2, y = b.y2 + 3 / z;
    ctx.fillStyle = "rgba(255,255,255,0.88)";
    roundRect(ctx, cx - tw / 2, y, tw, fs * 1.4, fs * 0.3);
    ctx.fill();
    ctx.fillStyle = "#111";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(o.name, cx, y + fs * 0.72);
  }
}

export const HANDLE = 8;
export function handles(o: Obj): { k: string; x: number; y: number }[] {
  const out: { k: string; x: number; y: number }[] = [];
  const pts: [string, number, number][] = [
    ["nw", 0, 0], ["n", 0.5, 0], ["ne", 1, 0], ["e", 1, 0.5], ["se", 1, 1], ["s", 0.5, 1], ["sw", 0, 1], ["w", 0, 0.5], ["rot", 0.5, -0.001],
  ];
  const cx = o.x + o.w / 2, cy = o.y + o.h / 2;
  const a = ((o.rot || 0) * Math.PI) / 180;
  for (const [k, fx, fy] of pts) {
    let lx = fx * o.w - o.w / 2, ly = fy * o.h - o.h / 2;
    if (k === "rot") ly -= 28;
    out.push({ k, x: cx + lx * Math.cos(a) - ly * Math.sin(a), y: cy + lx * Math.sin(a) + ly * Math.cos(a) });
  }
  return out;
}

function drawSelection(ctx: CanvasRenderingContext2D, o: Obj, z: number, single: boolean) {
  ctx.save();
  if (o.type === "wall") {
    const p = o.pts || [];
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 7 / z;
    ctx.globalAlpha = 0.35;
    ctx.beginPath();
    for (let i = 0; i < p.length; i += 2) i ? ctx.lineTo(p[i], p[i + 1]) : ctx.moveTo(p[i], p[i + 1]);
    ctx.stroke();
    ctx.globalAlpha = 1;
    for (let i = 0; i < p.length; i += 2) {
      ctx.fillStyle = "#fff";
      ctx.strokeStyle = "#07c";
      ctx.lineWidth = 2 / z;
      ctx.fillRect(p[i] - 4 / z, p[i + 1] - 4 / z, 8 / z, 8 / z);
      ctx.strokeRect(p[i] - 4 / z, p[i + 1] - 4 / z, 8 / z, 8 / z);
    }
    ctx.restore();
    return;
  }
  ctx.translate(o.x + o.w / 2, o.y + o.h / 2);
  ctx.rotate(((o.rot || 0) * Math.PI) / 180);
  ctx.strokeStyle = o.locked ? "#f55" : "#0af";
  ctx.lineWidth = 2 / z;
  ctx.setLineDash([6 / z, 4 / z]);
  ctx.strokeRect(-o.w / 2, -o.h / 2, o.w, o.h);
  ctx.setLineDash([]);
  if (single && !o.locked) {
    const s = HANDLE / z;
    ctx.fillStyle = "#fff";
    ctx.strokeStyle = "#07c";
    for (const [fx, fy] of [[0, 0], [0.5, 0], [1, 0], [1, 0.5], [1, 1], [0.5, 1], [0, 1], [0, 0.5]]) {
      const x = fx * o.w - o.w / 2, y = fy * o.h - o.h / 2;
      ctx.fillRect(x - s / 2, y - s / 2, s, s);
      ctx.strokeRect(x - s / 2, y - s / 2, s, s);
    }
    ctx.beginPath();
    ctx.moveTo(0, -o.h / 2);
    ctx.lineTo(0, -o.h / 2 - 28 / z + s / 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(0, -o.h / 2 - 28 / z, s * 0.7, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}

function drawPreview(ctx: CanvasRenderingContext2D, p: Preview, v: View) {
  const z = v.cam.z;
  ctx.save();
  const pts = p.pts;
  if (p.kind === "marquee") {
    ctx.fillStyle = "rgba(0,160,255,0.12)";
    ctx.strokeStyle = "#0af";
    ctx.lineWidth = 1 / z;
    const [x1, y1, x2, y2] = pts;
    ctx.fillRect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));
    ctx.strokeRect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));
  } else if (p.kind === "fog") {
    ctx.fillStyle = p.mode === "reveal" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.45)";
    ctx.strokeStyle = p.mode === "reveal" ? "#fff" : "#000";
    ctx.lineWidth = 2 / z;
    polyPath(ctx, pts);
    ctx.fill();
    ctx.stroke();
  } else if (p.kind === "wall") {
    ctx.strokeStyle = p.door ? "#f90" : "#3af";
    ctx.lineWidth = 4 / z;
    ctx.setLineDash([8 / z, 5 / z]);
    ctx.beginPath();
    for (let i = 0; i < pts.length; i += 2) i ? ctx.lineTo(pts[i], pts[i + 1]) : ctx.moveTo(pts[i], pts[i + 1]);
    ctx.stroke();
  } else {
    ctx.strokeStyle = v.style.stroke === "transparent" ? "#888" : v.style.stroke;
    ctx.lineWidth = v.style.strokeWidth || 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (v.style.fill !== "transparent") ctx.fillStyle = v.style.fill;
    ctx.beginPath();
    if (p.kind === "rect") ctx.rect(Math.min(pts[0], pts[2]), Math.min(pts[1], pts[3]), Math.abs(pts[2] - pts[0]), Math.abs(pts[3] - pts[1]));
    else if (p.kind === "ellipse") ctx.ellipse((pts[0] + pts[2]) / 2, (pts[1] + pts[3]) / 2, Math.abs(pts[2] - pts[0]) / 2, Math.abs(pts[3] - pts[1]) / 2, 0, 0, Math.PI * 2);
    else for (let i = 0; i < pts.length; i += 2) i ? ctx.lineTo(pts[i], pts[i + 1]) : ctx.moveTo(pts[i], pts[i + 1]);
    if (v.style.fill !== "transparent" && p.kind !== "path" && p.kind !== "line") ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}

function drawRuler(ctx: CanvasRenderingContext2D, r: Ruler, page: Page, z: number) {
  const [x1, y1, x2, y2] = r.pts;
  const len = Math.hypot(x2 - x1, y2 - y1);
  ctx.save();
  ctx.strokeStyle = r.color;
  ctx.fillStyle = r.color;
  ctx.lineWidth = 3 / z;
  ctx.globalAlpha = 0.9;
  if (r.shape === "circle") {
    ctx.globalAlpha = 0.25;
    ctx.beginPath(); ctx.arc(x1, y1, len, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 0.9; ctx.stroke();
  } else if (r.shape === "cone") {
    const a = Math.atan2(y2 - y1, x2 - x1), half = Math.atan(0.5);
    ctx.globalAlpha = 0.25;
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.arc(x1, y1, len, a - half, a + half); ctx.closePath(); ctx.fill();
    ctx.globalAlpha = 0.9; ctx.stroke();
  } else if (r.shape === "square") {
    const s = Math.max(Math.abs(x2 - x1), Math.abs(y2 - y1));
    const sx = x2 >= x1 ? x1 : x1 - s, sy = y2 >= y1 ? y1 : y1 - s;
    ctx.globalAlpha = 0.25; ctx.fillRect(sx, sy, s, s);
    ctx.globalAlpha = 0.9; ctx.strokeRect(sx, sy, s, s);
  }
  ctx.globalAlpha = 1;
  ctx.setLineDash([10 / z, 6 / z]);
  ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  ctx.setLineDash([]);
  const d = r.shape === "square" ? measure(page, x1, y1, x1 + Math.max(Math.abs(x2 - x1), Math.abs(y2 - y1)), y1) : measure(page, x1, y1, x2, y2);
  const label = `${d} ${page.units}${r.name ? ` · ${r.name}` : ""}`;
  const fs = 14 / z;
  ctx.font = `bold ${fs}px sans-serif`;
  const tw = ctx.measureText(label).width + fs;
  ctx.fillStyle = "rgba(20,20,24,0.85)";
  roundRect(ctx, x2 + 10 / z, y2 - fs * 1.8, tw, fs * 1.6, fs * 0.4);
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(label, x2 + 10 / z + fs / 2, y2 - fs);
  ctx.restore();
}
